import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, CheckCircle2, XCircle, AlertCircle, ClipboardList } from 'lucide-react';
import TabBar from '../../components/TabBar';
import { useToast } from '../../components/Toast';
import api from '../../utils/api';
import AdminNotificationBell from '../../components/AdminNotificationBell';

function formatDate(str) {
  if (!str) return '—';
  return str.slice(0, 16).replace('T', ' ');
}

export default function PhoneQueryPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [text, setText]       = useState(() => {
    // 从 OcrAdminDetail 的"溯源查询"按钮跳转时，sessionStorage 里有预填号码
    try {
      const prefill = sessionStorage.getItem('phoneQueryPrefill') || '';
      if (prefill) { sessionStorage.removeItem('phoneQueryPrefill'); return prefill; }
    } catch {}
    return '';
  });
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // 解析：每行提取纯数字，最多11位
  const parsed = useMemo(() => {
    return text.split('\n').map((line, idx) => {
      const digits  = line.replace(/\D/g, '').slice(0, 11);
      const isEmpty = digits === '';
      const isValid = /^1[3-9]\d{9}$/.test(digits);
      return { idx, raw: line, digits, isEmpty, isValid };
    });
  }, [text]);

  const nonEmptyLines = parsed.filter(p => !p.isEmpty);
  const validPhones   = nonEmptyLines.filter(p => p.isValid).map(p => p.digits);
  const invalidLines  = nonEmptyLines.filter(p => !p.isValid);
  const uniquePhones  = [...new Set(validPhones)];
  const dupCount      = validPhones.length - uniquePhones.length;

  async function handleQuery() {
    if (uniquePhones.length === 0) { showToast('请输入至少一个手机号', 'error'); return; }
    if (uniquePhones.length > 500) { showToast('单次最多查询 500 个', 'error'); return; }
    if (invalidLines.length > 0)   { showToast('有行格式有误，请检查后再查询', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/dispatch/query-phones', { phones: uniquePhones });
      setResults(res.data.results);
      setSummary({ total: res.data.total, found: res.data.found, legacy: res.data.foundLegacy || 0, puzzle: res.data.foundPuzzle || 0 });
    } catch (err) {
      showToast(err.response?.data?.error || '查询失败', 'error');
    } finally {
      setLoading(false);
    }
  }

  function handleClear() {
    setText('');
    setResults(null);
    setSummary(null);
  }

  const foundItems = results?.filter(r => r.found)  || [];
  const missItems  = results?.filter(r => !r.found) || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>

      {/* 顶部导航 */}
      <div className="header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginLeft: -8, width: 'auto' }}>
          <ChevronLeft size={20} />
        </button>
        <span style={{ flex: 1 }}>手机号溯源查询</span>
        <AdminNotificationBell />
      </div>

      {/* 可滚动内容区 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', paddingTop: 'calc(46px + env(safe-area-inset-top, 0px) + 12px)', paddingBottom: 90 }}>

        {/* 输入 card */}
        <div className="card" style={{ padding: 14 }}>

          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
            每行一个号码，支持从表格、记事本直接粘贴
          </div>

          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={'13800138000\n13900139000\n...'}
            rows={7}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px',
              fontFamily: 'monospace', fontSize: 14, lineHeight: 1.9,
              border: `1.5px solid ${invalidLines.length > 0 ? 'var(--c-danger)' : 'var(--c-border)'}`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--c-bg)',
              color: 'var(--c-text)',
              resize: 'vertical',
              outline: 'none',
              transition: 'border-color .15s',
            }}
            onFocus={e => { if (invalidLines.length === 0) e.target.style.borderColor = 'var(--c-primary)'; }}
            onBlur={e  => { e.target.style.borderColor = invalidLines.length > 0 ? 'var(--c-danger)' : 'var(--c-border)'; }}
          />

          {/* 解析状态行 */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '8px 0 12px', fontSize: 12, alignItems: 'center' }}>
            {uniquePhones.length > 0 && (
              <span style={{ color: 'var(--c-success)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle2 size={13} strokeWidth={2} /> 有效 {uniquePhones.length} 个
              </span>
            )}
            {dupCount > 0 && (
              <span style={{ color: 'var(--c-accent)' }}>
                重复 {dupCount} 个（已自动去重）
              </span>
            )}
            {invalidLines.length > 0 && (
              <span style={{ color: 'var(--c-danger)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <XCircle size={13} strokeWidth={2} /> 格式有误 {invalidLines.length} 行：
                {invalidLines.slice(0, 3).map(l => `第 ${l.idx + 1} 行`).join('、')}
                {invalidLines.length > 3 ? ' 等' : ''}
              </span>
            )}
            {text.trim() === '' && (
              <span style={{ color: 'var(--c-text-tertiary)' }}>输入号码后开始查询</span>
            )}
          </div>

          {/* 查询 / 清空 */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              onClick={handleQuery}
              disabled={loading || uniquePhones.length === 0 || invalidLines.length > 0}
              style={{ flex: 1, height: 44, width: 'auto' }}
            >
              <Search size={16} style={{ marginRight: 4 }} />
              {loading ? '查询中…' : `查询${uniquePhones.length > 0 ? `（${uniquePhones.length} 个）` : ''}`}
            </button>
            {(text !== '' || results !== null) && (
              <button
                onClick={handleClear}
                style={{
                  height: 44, padding: '0 20px', flexShrink: 0,
                  background: 'none', border: '1.5px solid var(--c-border)',
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  color: 'var(--c-text-secondary)', fontWeight: 600, fontSize: 14,
                }}
              >
                清空
              </button>
            )}
          </div>
        </div>

        {/* 汇总 */}
        {summary !== null && (
          <div className="card" style={{ display: 'flex', padding: '12px 0' }}>
            <div className="stat-card" style={{ flex: 1 }}>
              <ClipboardList size={18} color="var(--c-primary)" style={{ margin: '0 auto 4px' }} />
              <div className="stat-value">{summary.total}</div>
              <div className="stat-label">共查询</div>
            </div>
            <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
            <div className="stat-card" style={{ flex: 1 }}>
              <CheckCircle2 size={18} color="var(--c-success)" style={{ margin: '0 auto 4px' }} />
              <div className="stat-value" style={{ color: 'var(--c-success)' }}>{summary.found}</div>
              <div className="stat-label">找到记录</div>
              <div style={{ fontSize: 10, color: 'var(--c-text-tertiary)', marginTop: 2 }}>排五 {summary.legacy} · 证券 {summary.puzzle}</div>
            </div>
            <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
            <div className="stat-card" style={{ flex: 1 }}>
              <XCircle size={18} color="var(--c-danger)" style={{ margin: '0 auto 4px' }} />
              <div className="stat-value" style={{ color: 'var(--c-danger)' }}>{summary.total - summary.found}</div>
              <div className="stat-label">无记录</div>
            </div>
          </div>
        )}

        {/* 找到的结果 */}
        {foundItems.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px 8px', fontWeight: 700, fontSize: 14, borderBottom: '1px solid var(--c-border)' }}>
              <CheckCircle2 size={14} color="var(--c-success)" style={{ verticalAlign: 'middle', marginRight: 6 }} />
              已找到 {foundItems.length} 个号码的记录（排五派发 / 证券数据包）
            </div>
            {foundItems.map((item, idx) => (
              <PhoneResultItem key={idx} item={item} last={idx === foundItems.length - 1} />
            ))}
          </div>
        )}

        {/* 未找到 */}
        {missItems.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px 8px', fontWeight: 700, fontSize: 14, borderBottom: '1px solid var(--c-border)', color: 'var(--c-danger)' }}>
              <XCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {missItems.length} 个号码无记录
            </div>
            <div style={{ padding: '10px 16px 14px' }}>
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
                <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                可能原因：该号码从未派发（排五）、不在任何证券数据包里、已清除隐私数据，或不在您的管理范围内
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {missItems.map((item, idx) => (
                  <span key={idx} style={{
                    fontSize: 13, fontFamily: 'monospace',
                    background: 'var(--c-danger-light, #fff0f0)',
                    color: 'var(--c-danger)', borderRadius: 6,
                    padding: '3px 8px', border: '1px solid var(--c-danger)', opacity: 0.8,
                  }}>
                    {item.phone}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      <TabBar mode="admin" />
    </div>
  );
}

function BizTag({ biz }) {
  const puzzle = biz === 'puzzle';
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '1px 7px', marginRight: 6, flexShrink: 0,
      background: puzzle ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)',
      color: puzzle ? '#8944ab' : 'var(--c-primary)',
    }}>{puzzle ? '证券' : '排五'}</span>
  );
}

const PUZZLE_STATUS = {
  counted:   { label: '已计入', cls: 'badge-success' },
  pending:   { label: '待核实', cls: 'badge-warning' },
  draft:     { label: '草稿（未确认）', cls: 'badge-info' },
  held:      { label: '已领取·未提交', cls: 'badge-info' },
  unclaimed: { label: '数据包未领取', cls: 'badge' },
};

function PhoneResultItem({ item, last }) {
  const r = item.records && item.records[0];
  const puzzle = item.puzzle || [];
  return (
    <div style={{ padding: '12px 16px', borderBottom: last ? 'none' : '1px solid var(--c-border)' }}>
      <div style={{ fontSize: 14, fontFamily: 'monospace', fontWeight: 700, marginBottom: 6 }}>{item.phone}</div>

      {r && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: puzzle.length ? 10 : 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <BizTag biz="legacy" />
              <span style={{ fontWeight: 600 }}>{r.user_name || r.user_phone}</span>
              <span style={{ color: 'var(--c-text-secondary)', marginLeft: 4, fontSize: 12 }}>({r.user_phone})</span>
              {r.is_external ? <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 11 }}>外部</span> : null}
            </div>
            {r.agent_name && (
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                代理商：{r.agent_name}（{r.agent_phone}）
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
              {r.dispatch_date} · 派发于 {formatDate(r.dispatched_at)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 2 }}>
              数据添加时间：{r.data_imported_at ? formatDate(r.data_imported_at) : '未知（历史数据）'}
            </div>
          </div>
          <StatusBadge status={r.record_status} />
        </div>
      )}

      {puzzle.map((p, i) => {
        const st = PUZZLE_STATUS[p.status] || { label: p.status, cls: 'badge' };
        return (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: i ? 8 : 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <BizTag biz="puzzle" />
                {p.user_name
                  ? <><span style={{ fontWeight: 600 }}>{p.user_name}</span><span style={{ color: 'var(--c-text-secondary)', marginLeft: 4, fontSize: 12 }}>({p.user_phone})</span></>
                  : <span style={{ color: 'var(--c-text-secondary)' }}>还没有人领取</span>}
                {p.is_external ? <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 11 }}>外部</span> : null}
              </div>
              {p.agent_name && (
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                  代理商：{p.agent_name}（{p.agent_phone}）
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                数据包「{p.package_name}」{p.claimed_at ? ` · 领取于 ${formatDate(p.claimed_at)}` : ''}
              </div>
              {p.confirmed_at && (
                <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginTop: 2 }}>
                  提交于 {formatDate(p.confirmed_at)}{p.seq_no != null ? ` · 当天第 ${p.seq_no} 单` : ''}
                </div>
              )}
            </div>
            <span className={`badge ${st.cls}`} style={{ flexShrink: 0 }}>{st.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:    { label: '进行中', cls: 'badge-warning' },
    completed: { label: '已完成', cls: 'badge-success' },
    repushed:  { label: '已重推', cls: 'badge-info' },
  };
  const cfg = map[status] || { label: status, cls: 'badge' };
  return <span className={`badge ${cfg.cls}`}>{cfg.label}</span>;
}
